import { CONSENT_STORAGE_KEY } from './consent.constants';
import type { ServiceConsentState } from './consent.types';
import { persistConsent, readStoredConsent } from './consent-storage';

/**
 * A tiny external store over localStorage so the provider can read persisted
 * consent with `useSyncExternalStore` — SSR-safe (no hydration flash, no
 * setState-in-effect) and synchronized across browser tabs for free.
 *
 * It holds the raw per-service decisions only. Categories are derived in the
 * provider, which is where the active service catalog is known.
 */
export type ConsentSnapshot = {
    /** False during SSR and the first client render, true once read on the client. */
    hydrated: boolean;
    serviceConsent: ServiceConsentState;
    decided: boolean;
};

const SERVER_SNAPSHOT: ConsentSnapshot = {
    hydrated: false,
    serviceConsent: {},
    decided: false,
};

const listeners = new Set<() => void>();

// getSnapshot must return a referentially-stable value while the underlying data
// is unchanged, otherwise React re-renders endlessly. Cache by the raw string.
let cachedRaw: string | null | undefined;
let cachedSnapshot: ConsentSnapshot = SERVER_SNAPSHOT;

function getServerSnapshot(): ConsentSnapshot {
    return SERVER_SNAPSHOT;
}

function getSnapshot(): ConsentSnapshot {
    if (typeof window === 'undefined') return SERVER_SNAPSHOT;

    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw === cachedRaw && cachedSnapshot.hydrated) return cachedSnapshot;

    cachedRaw = raw;
    const stored = readStoredConsent();
    cachedSnapshot = stored
        ? { hydrated: true, serviceConsent: stored.serviceConsent, decided: stored.decided }
        : { hydrated: true, serviceConsent: {}, decided: false };

    return cachedSnapshot;
}

function emitChange() {
    cachedRaw = undefined; // force a re-read on the next getSnapshot
    for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);

    const onStorage = (event: StorageEvent) => {
        if (event.key === CONSENT_STORAGE_KEY) emitChange();
    };
    window.addEventListener('storage', onStorage);

    return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', onStorage);
    };
}

/** Persist a consent decision and notify all subscribers in this tab. */
export function writeConsent(serviceConsent: ServiceConsentState, decided: boolean): void {
    persistConsent(serviceConsent, decided);
    emitChange();
}

export const consentStore = {
    subscribe,
    getSnapshot,
    getServerSnapshot,
};
